import { BreakpointState } from "@angular/cdk/layout";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    effect,
    ElementRef,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    signal,
    Signal,
    ViewChild,
} from "@angular/core";

import { toSignal } from "@angular/core/rxjs-interop";
import { Subscription } from "rxjs";

import {
    BreakpointsConfiguration,
    CarouselComponent,
    ClickOnAssetEvent,
    SwiperCustom,
} from "@hermes/aphrodite/carousel";
import { TRANSPARENT_COLOR } from "@hermes/aphrodite/expansion-panel";
import { IconButtonSize, MEDIUM } from "@hermes/aphrodite/icon-button";

import { Asset } from "@hermes/api-model-asset";

import {
    Product,
    ProductEdito,
    ProductToggles,
} from "@hermes/api-model-product";
import { Context, WINDOW } from "@hermes/app-core";
import {
    PRODUCT_IN_GRID_DESKTOP,
    BackToGridService,
    PRODUCT_PAGE_IMAGE_SIZE,
} from "@hermes/fragments/product-utils";
import { AddToCartFacade } from "@hermes/states/add-to-cart";
import {
    ProductPageAssetService,
    ProductPageFacade,
} from "@hermes/states/product-page";
import { VtoFacade } from "@hermes/states/vto";
import {
    AssetMediaType,
    ENTER,
    LEFT,
    RIGHT,
} from "@hermes/utils-generic/constants";
import { stopEventPropagation } from "@hermes/utils-generic/helpers";
import { RouterService } from "@hermes/utils-generic/services/router";
import { BreakpointService } from "@hermes/utils-generic/services/user-interface";

// Constants to define the number of visible slides based on screen size
// For desktop mode:
// - 2.22 slides are shown to allow the main display of the first two slides at 45% each,
//   and a partial view of the third slide at 10%
export const SLIDE_PER_VIEW_DESKTOP: number = 2.22;
const SLIDE_PER_VIEW_MOBILE: number = 1;

@Component({
    selector: "h-product-template",
    templateUrl: "./product-template.component.html",
    styleUrl: "./product-template.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductTemplateComponent implements OnInit, OnDestroy {
    @ViewChild("carousel")
    public hCarousel!: CarouselComponent;

    @ViewChild("stickyElement")
    public stickyElement!: ElementRef<HTMLDivElement>;

    @ViewChild("wrapperElement")
    public wrapperElement!: ElementRef<HTMLDivElement>;

    @Input() public productReference?: string;
    @Input() public isComposite = false;
    @Input() public hasExternalItems = false;
    @Input()
    public selectedCarouselIndex: number = 0;
    @Input() public backgroundWhite: boolean = false;
    @Input() public displayFooterDetail: boolean = true;

    public readonly RIGHT_PANEL_WIDTH: number = 427;

    public controlSize: IconButtonSize = MEDIUM;
    public assetMediaType = AssetMediaType;

    public carouselAssetsSignal: Signal<Asset[]>;
    public startVideo = false;

    public isMobile: boolean = false;
    public isServer: boolean = false;
    public displayBackButton: boolean = false;
    public isVtoEnabled = false;
    public hasTransition = false;
    public storyBehind: ProductEdito[] | undefined;
    public productToggles: ProductToggles | undefined;

    public imagesIndex: number = 0;
    public carouselIndex: number = 0;
    public reverseAnimationMargin: boolean = false;
    public animationMargin: boolean = false;
    public slidePerViewDesktop: number = SLIDE_PER_VIEW_DESKTOP;
    public slidePerViewMobile: number = SLIDE_PER_VIEW_MOBILE;

    public breakpontsConfiguration: BreakpointsConfiguration = {
        0: {
            slidesPerView: 1,
            slidesPerGroup: 1,
            freeMode: {
                enabled: false,
            },
        },
        768: {
            slidesPerView: "auto",
            slidesPerGroup: 1,
            longSwipesRatio: 0.2,
            touchRatio: 0.5,
            freeMode: {
                enabled: true,
            },
            speed: 600,
        },
    };

    // We store the last carousel calculated positions.
    public lastSnapGrid: number[] = [];

    // We update videoPosterUrl at each assets reload.
    public getVideoPosterUrl = computed(() => (index: number) => {
        const asset = this.carouselAssetsSignal()[index];
        return asset?.posterUrl
            ? `${asset.posterUrl}?wid=${PRODUCT_IN_GRID_DESKTOP}&hei=${PRODUCT_IN_GRID_DESKTOP}`
            : "";
    });

    // We add little space after slides when only 2 assets.
    public readonly slidesOffsetSignal = computed(() =>
        !this.isMobile && this.carouselAssetsSignal().length === 2
            ? this.RIGHT_PANEL_WIDTH
            : 0,
    );

    public productInputSignal = signal<Product>({} as Product);

    // We need mousewheel to work only in horizontal mode.
    public readonly MOUSEWHEEL_OPTIONS = { forceToAxis: true };

    public readonly productImageIntrinsecSize = PRODUCT_PAGE_IMAGE_SIZE;

    protected readonly transparentColor = TRANSPARENT_COLOR;

    private readonly subscription = new Subscription();

    constructor(
        public readonly productPageFacade: ProductPageFacade,
        private readonly productPageAssetService: ProductPageAssetService,
        private readonly breakpointService: BreakpointService,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly context: Context,
        private readonly vtoFacade: VtoFacade,
        private readonly backToGridService: BackToGridService,
        private readonly addToCartFacade: AddToCartFacade,
        private readonly routerService: RouterService,
        @Inject(WINDOW) private window: Window,
        private ngZone: NgZone,
    ) {
        const transformedAssetsSignal = toSignal(
            this.productPageAssetService.transformedAssets$,
            { initialValue: [] },
        );

        this.carouselAssetsSignal = computed(() => {
            const product = this.productInputSignal();
            return product?.personalize && product?.assets?.length
                ? product.assets
                : transformedAssetsSignal();
        });

        effect(() => {
            const assets = this.carouselAssetsSignal();
            if (assets.length > 0) {
                this.startVideo = false;

                // We need to force carousel to update this value.
                this.hCarousel.swiper?.updateParameter(
                    "slidesOffsetAfter",
                    this.slidesOffsetSignal(),
                );
                this.imagesIndex = this.selectedCarouselIndex;
                this.handleImageGalleryChangeIndex(this.selectedCarouselIndex);
                this.changeDetector.detectChanges();
            } else {
                this.updateCarousel();
            }
        });
    }

    @Input()
    public set product(product: Product) {
        this.productInputSignal.set(product);
        this.isVtoEnabled = !!(product?.isVto || product?.vtoType);

        if (this.isVtoEnabled && !!product.vtoType) {
            this.vtoFacade.updateCurrentVtoType(product.vtoType);
        }
    }

    public ngOnInit(): void {
        this.isServer = this.context.isInServerMode();
        this.displayBackButton = this.backToGridService.hasGridUrlKey();

        this.storyBehind = this.productInputSignal()?.content?.storyBehind;
        this.productToggles = this.productInputSignal()?.content?.toggles;

        if (!this.isServer) {
            this.subscription.add(
                this.breakpointService
                    .mediumBreakpointObserver()
                    .subscribe((value: BreakpointState) => {
                        this.isMobile = !value.matches;
                        this.updateCarousel();
                    }),
            );
        }
    }

    public handleSlideChange(carouselCurrentIndex: number): void {
        this.carouselIndex = carouselCurrentIndex;
        this.productPageAssetService.sendAnalyticsTagForCarouselChangeImage(
            carouselCurrentIndex,
            this.productInputSignal()?.templateType,
            this.productInputSignal()?.assets ?? [],
        );

        // Dependency between image gallery and carousel.
        this.imagesIndex = carouselCurrentIndex;

        // We start the video as soon it is visible.
        if (carouselCurrentIndex <= 1) {
            this.startVideo = true;
        }
        this.changeDetector.detectChanges();
    }

    public handleKeyNavigation(
        event: KeyboardEvent,
        clickOnAssetEvent: ClickOnAssetEvent,
    ) {
        this.carouselIndex = this.hCarousel.swiper.swiperRef.realIndex;
        switch (event.key) {
            case RIGHT: {
                this.changeSelectionRight();
                this.stopPropagation(event);
                break;
            }
            case LEFT: {
                this.changeSelectionLeft(
                    clickOnAssetEvent.carouselCurrentIndex ??
                        this.carouselIndex,
                );
                this.stopPropagation(event);
                break;
            }
            case ENTER: {
                this.openZoomModal(clickOnAssetEvent);
                this.stopPropagation(event);
                break;
            }
            default: {
                break;
            }
        }
    }

    public handleImageGalleryChangeIndex(imageIndex: number): void {
        // Dependency between image gallery and carousel.
        this.carouselIndex = imageIndex;
        this.updateCarousel(imageIndex);
    }

    public handleGoBack(): void {
        this.productPageFacade.goBack();
        this.addToCartFacade.closeNotification();
        this.routerService.spaRedirect(
            this.backToGridService.getUrlPreviousGrid(),
        );
    }

    public openZoomModal(clickOnAssetEvent: ClickOnAssetEvent): void {
        this.handleFocus();
        if (clickOnAssetEvent.assetType === AssetMediaType.Image) {
            this.productPageFacade.toggleZoom({
                zoomStatus: true,
                currentIndex: clickOnAssetEvent.carouselCurrentIndex,
                zoomAssets: this.productInputSignal()?.assets,
                isTopLevelProduct: true,
                templateType: this.productInputSignal()?.templateType,
            });
        }
    }

    public stopPropagation(event: Event): void {
        stopEventPropagation(event);
        event.stopImmediatePropagation();
    }

    public handleFocus(): void {
        if (this.context.isInBrowserMode()) {
            this.window.scrollTo(0, 0);
        }
    }

    public onImageReady(): void {
        this.hasTransition = true;
    }

    public trackByAssetIndex(index: number): string {
        return `asset_${index}`;
    }

    public onSliderFirstMove() {
        // We force grid update at first carousel interaction
        // in case transitionEnd or imageGalleryChange hasn't been triggered.
        this.updateSnapGrid();
        // Start video at first carousel interaction.
        if (!this.isMobile) {
            this.startVideo = true;
        }
    }

    /**
     * Allows to correct SwiperJs miscalculation in case of decimal slidesPerView.
     * We override snapGrid and slidesGrid objects to handle the last slide correctly.
     */
    public updateSnapGrid(): void {
        // We don't need to update snap grid on mobile because slidesPerView is integer.
        // We don't need to update snap grid if we have less than 2 assets.
        if (
            this.isMobile ||
            this.carouselAssetsSignal().length < 2 ||
            this.productPageFacade.isProductPageSelectorRevampEnabled
        ) {
            return;
        }

        const swiperReference = this.hCarousel?.swiper?.swiperRef;

        if (!swiperReference) {
            return;
        }

        const updatedSnapGrid = [...swiperReference.snapGrid];
        // Snap grid has been updated by swiper, we need to rebuild it.
        if (
            updatedSnapGrid.length !== this.lastSnapGrid.length ||
            !updatedSnapGrid.every(
                (value, index) => value === this.lastSnapGrid[index],
            )
        ) {
            const updatedGrid = [
                ...swiperReference.slidesGrid.slice(0, -1),
                this.getLastSlideSnapGridPosition(swiperReference),
            ];

            this.lastSnapGrid = updatedGrid;

            // We override the swiper snapGrid and slidesGrid.
            swiperReference.slidesGrid = [...updatedGrid];
            swiperReference.snapGrid = [...updatedGrid];
        }
    }

    public onExpandToggle(
        event: { id: string; isExpanded: boolean } | undefined,
    ): void {
        if (event) {
            this.productPageFacade.showMoreVariant({
                id: event.id,
                isExpanded: event.isExpanded,
            });
        }
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    private updateCarousel(index?: number) {
        this.updateSnapGrid();
        this.ngZone.runOutsideAngular(() => {
            setTimeout(() => {
                if (this.hCarousel) {
                    if (index !== undefined || this.carouselIndex >= 0) {
                        this.slideTo(index ?? 0);
                    }
                    this.hCarousel.updateSlides();
                    this.changeDetector.markForCheck();
                }
            });
        });
    }

    private changeSelectionLeft(index: number): void {
        if (this.carouselIndex > 0) {
            this.carouselIndex =
                this.carouselIndex === index
                    ? this.carouselIndex - 1
                    : index - 1;
            this.focusSlide(this.carouselIndex);
        }
    }

    private changeSelectionRight(): void {
        const productAssets = this.productInputSignal()?.assets;
        if (productAssets && this.carouselIndex < productAssets.length) {
            this.carouselIndex = this.carouselIndex + 1;
            this.focusSlide(this.carouselIndex);
        }
    }

    private focusSlide(index: number): void {
        this.slideTo(index);
        const slide = document.getElementById(`slide-${index}`);
        slide?.focus();
        this.changeDetector.detectChanges();
    }

    private slideTo(index: number): void {
        if (!this.isServer) {
            this.hCarousel.swiper.swiperRef.slideTo(index, 400);
        }
    }

    /**
     * We calculate the new position of the last slide to fit with sticky panel.
     * @param swiperReference
     */
    private getLastSlideSnapGridPosition(swiperReference: SwiperCustom) {
        const slideWidth = swiperReference.slidesGrid[1];
        return (
            (swiperReference.slidesGrid.at(-1) as number) -
            (SLIDE_PER_VIEW_DESKTOP - 1) * slideWidth +
            this.RIGHT_PANEL_WIDTH
        );
    }
}
